import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useAsync} from "react-use";
import { Loader2 } from "lucide-react";
import {usePlayback, useWebsocket} from "../../base/hooks";
import {EventType} from "../../../apis";
import {useWebsocketContext, WebsocketProvider} from "../session/WebsocketContext";
import {useSessionEventListener} from "../../base/hooks/useSessionEventListener";
import {Select} from "../../base/inputs/select";
import {useSupportedLanguages} from "../../base/hooks/useSupportedLanguages";
import {SessionStateProvider, useSessionState} from "../session/SessionStateContext";
import {PlayPauseButton} from "../../base/PlayPauseButton";
import {Stenographer} from "../../base";
import {OutputConfigProvider, useOutputConfig} from "./OutputConfigContext";
import {SettingsButton} from "./SettingsButton";
import {useApiClient} from "../../base/hooks/useApiClient";
import {ZoomProvider} from "../session/ZoomContext";

interface SessionListenPageContentProps {
    language: string;
    onLanguageChange: (language: string) => void;
}

function SessionListenPageContent({ language, onLanguageChange }: SessionListenPageContentProps) {
    const { sessionUuid } = useParams<{ sessionUuid: string }>();
    const { websocket } = useWebsocketContext();
    const { isSessionRunning } = useSessionState();
    const [outputConfig] = useOutputConfig();
    const { playback } = usePlayback();
    const { supportedLanguages } = useSupportedLanguages();
    const apiClient = useApiClient();
    
    const { value: session, loading: sessionLoading } = useAsync(async () => {
        const response = await apiClient.get(`sessions/${sessionUuid}`);

        if (!response.ok) {
            console.error('Error fetching session data');
        }

        const session = await response.json();

        return session as { name: string, originalLanguage: string, translationLanguages: string[] };
    });

    useSessionEventListener(EventType.ForceStopSession, async () => {
        await onStopPlaying();
    });

    const handleJoin = async () => {
        await playback.enable(outputConfig.device?.deviceId, outputConfig.stereo);
        await websocket.send(EventType.StartListening);
    };

    const onStopPlaying = async () => {
        playback.disable();
        await websocket.send(EventType.StopListening);
    };

    if (sessionLoading || !session) {
        return (
            <div className="fixed inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-gray-600" />
            </div>
        );
    }

    const languageOptions = session.translationLanguages.length === 0 ? supportedLanguages : supportedLanguages.filter(supportedLanguage => {
        return session.translationLanguages.includes(supportedLanguage.value);
    });

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <div className="fixed top-0 left-0 right-0 bg-white shadow-sm p-4 flex justify-between items-center z-10">
                <h2 className="whitespace-nowrap text-ellipsis overflow-hidden text-xl font-bold text-gray-900">{session.name}</h2>
                <div className="flex space-x-4 items-center">
                    <SettingsButton />
                    <Select
                        disabled={isSessionRunning}
                        options={languageOptions}
                        onChange={(option) => onLanguageChange(option.value)}
                    />
                    <PlayPauseButton
                        disabled={!language}
                        disableReason="You need to select a language first"
                        playText=""
                        pauseText=""
                        playClassName="w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
                        pauseClassName="w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors bg-red-100 text-red-700 hover:bg-red-200"
                        onStart={handleJoin}
                        onStop={onStopPlaying}
                    />
                </div>
            </div>
            
            {/* Spacer to prevent content from hiding under fixed header */}
            <div className="h-[74px]"></div>

            <div className="flex-1">
                {/* we need an extra 80 px to support mobile */}
                <Stenographer
                    eventType={EventType.Translation}
                    className="max-h-[calc(100vh-154px)] sm:max-h-[calc(100vh-74px)]"
                    placeholderJoin="Join the session and wait for the translation"
                    placeholderTextAppear="Translation will appear here..."
                />
            </div>
        </div>
    );
}

export function SessionListenPage() {
    const [language, setLanguage] = useState<string>('');
    const { sessionUuid } = useParams<{ sessionUuid: string }>();
    const websocket = useWebsocket(`/sessions/${sessionUuid}/${language}`, !!language);

    return (
        <WebsocketProvider websocket={websocket}>
            <SessionStateProvider>
                <ZoomProvider>
                    <OutputConfigProvider>
                        <SessionListenPageContent language={language} onLanguageChange={newLanguage => setLanguage(newLanguage)} />
                    </OutputConfigProvider>
                </ZoomProvider>
            </SessionStateProvider>
        </WebsocketProvider>
    );
}
